import { Input } from "components/EmbeddedForm/forms/GenericForm"
import Checkbox from "components/shared/Checkbox"
import CountryCodeInput from "components/shared/inputs/CountryCodeInput"
import Loading from "components/shared/Loading"
import { Button } from "components/shared/panda/Button"
import { getPath } from "lib/helpers"
import Link from "next/link"
import { useRouter } from "next/router"
import { Trans, useTranslation } from "next-i18next"
import { FormEvent, useEffect, useRef, useState } from "react"
import Captcha from "react-google-recaptcha"
import { VStack } from "styled-system/jsx"

import { ContactFormProps } from "../ContactForm"
import FormWrapper, { FormResponse } from "../FormWrapper"

const CAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY

type Props = ContactFormProps & {
  extraData?: Record<string, unknown>
}

function CreateAccountForm({ customForm, extraData }: Props) {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<FormResponse>()
  const [captchaSuccess, setCaptchaSuccess] = useState(false)

  const { t } = useTranslation()
  const { query, locale, push } = useRouter()

  const handleCaptcha = (key: string) => {
    setCaptchaSuccess(true)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const element = event.target as HTMLFormElement

    if (!captchaSuccess) {
      setData({
        error: true,
        message: "Debes confirmar que eres humano",
      })
      return
    }

    setData(null)

    const data = {
      name: element.name.value,
      first_name: element.firstName.value,
      last_name: element.lastName.value,
      email: element.email.value,
      main_country_code: element.mainCountryCode.value,
      password: element.password.value,
      passwordConfirmation: element.passwordConfirmation.value,
      terms: element.terms.checked,
      ...extraData,
    }

    const endpoint = "/api/create-account"

    const options = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }

    setLoading(true)
    const response = await fetch(endpoint, options)

    const result = await response.json()

    setData(result)

    if (result.error) event.nativeEvent.stopImmediatePropagation()
    else {
      setTimeout(() => {
  element?.reset()
}, 500)

      if (customForm.thankYouPage) push(customForm.thankYouPage as string)
    }

    setLoading(false)
  }

  const formRef = useRef<HTMLFormElement>()
  useEffect(() => {
    if (!formRef.current) return

    Object.entries(query).forEach(([key, value]) => {
      const field = formRef.current.elements.namedItem(key)
      if (field && value && "value" in field && typeof value === "string")
        field.value = value
    })
  }, [query])

  return (
    <FormWrapper
      ref={formRef}
      id={customForm?.formId || "create-account-form"}
      method="POST"
      action="/api/create-account"
      onSubmit={handleSubmit}
      data={data}>
      <VStack alignItems="start" gap={6}>
        <Input name="name" label={t("name")} />
        <Input name="firstName" label={t("firstname")} />
        <Input name="lastName" label={t("lastname")} />
        <Input name="email" label={t("email")} />
        <CountryCodeInput
          name="mainCountryCode"
          renderLabel={x => x.label}
          allowedCountries={["ES", "MX", "CL", "PE", "CO"]}
        />
        <Input name="password" label={t("password")} />
        <Input name="passwordConfirmation" label={t("passwordConfirmation")} />
      </VStack>

      <VStack alignItems="stretch" gap={4}>
        <Checkbox
          label={
            <Trans
              i18nKey="termsLabel"
              components={{
                terms: <Link href={getPath("termsConditions", locale)} />,
                privacy: <Link href={getPath("privacyPolicy", locale)} />,
              }}
            />
          }
          name="terms"
        />

        {CAPTCHA_SITE_KEY && (
          <Captcha sitekey={CAPTCHA_SITE_KEY} onChange={handleCaptcha} />
        )}

        <Button
          variant="filled"
          fillColor="primary"
          color="white"
          disabled={loading}>
          {loading ? <Loading /> : customForm?.submitLabel || t("submit")}
        </Button>
      </VStack>
    </FormWrapper>
  )
}

export default CreateAccountForm
export type { Props as CreateAccountFormProps }
